<template>
    <div class="s3-multi-image-upload">
        <div class="d-flex flex-wrap">
            <div
                v-for="(img, idx) in images"
                :key="idx"
                class="s3-multi-image-upload__tile border rounded mr-2 mb-2 p-1"
                :style="tileStyle(idx)"
            >
                <img
                    :src="img.cdn_url"
                    alt="image"
                    :style="imageStyle(idx)"
                    class="rounded"
                />
                <div class="d-flex justify-content-between align-items-center mt-1">
                    <base-button
                        size="sm"
                        type="secondary"
                        :disabled="idx === 0"
                        @click="moveUp(idx)"
                        style="padding: 2px 6px;"
                    >
                        <i class="fa fa-arrow-left"></i>
                    </base-button>
                    <base-button
                        size="sm"
                        type="secondary"
                        :disabled="idx === images.length - 1"
                        @click="moveDown(idx)"
                        style="padding: 2px 6px;"
                    >
                        <i class="fa fa-arrow-right"></i>
                    </base-button>
                    <base-button
                        size="sm"
                        type="danger"
                        @click="removeImage(idx)"
                        style="padding: 2px 6px;"
                    >
                        <i class="fa fa-times"></i>
                    </base-button>
                </div>
            </div>

            <div
                v-if="images.length < max"
                class="s3-multi-image-upload__add border rounded mr-2 mb-2 d-flex align-items-center justify-content-center"
                style="width: 120px; height: 120px; cursor: pointer; background: #f8f9fa;"
                @click="triggerFileInput"
            >
                <i class="fa fa-plus fa-2x text-muted"></i>
            </div>
        </div>

        <input
            ref="fileInput"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style="display: none;"
            @change="handleFileChange"
        />

        <el-progress
            v-if="uploading"
            :percentage="uploadProgress"
            class="mt-2"
        ></el-progress>

        <div v-if="errorMsg" class="text-danger small mt-1">{{ errorMsg }}</div>
    </div>
</template>

<script>
import { Progress } from "element-ui";
import { AppCatalogUploadService } from "../../resource";

export default {
    name: "S3MultiImageUpload",
    components: {
        [Progress.name]: Progress
    },
    props: {
        objectType: {
            type: String,
            required: true
        },
        value: {
            type: Array,
            default: () => []
        },
        max: {
            type: Number,
            default: 10
        },
        // Seller catalog edit pages: the first image (the main one) renders at
        // 220px square, the rest of the gallery at 120px. Off by default.
        large: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            uploading: false,
            uploadProgress: 0,
            errorMsg: ""
        };
    },
    computed: {
        images() {
            return this.value || [];
        }
    },
    methods: {
        tileWidth(idx) {
            if (!this.large) return 120;
            return idx === 0 ? 220 : 120;
        },
        tileStyle(idx) {
            return "width: " + this.tileWidth(idx) + "px; position: relative;";
        },
        imageStyle(idx) {
            if (!this.large) return "width: 100%; height: 80px; object-fit: cover;";
            // tile width minus the p-1 padding + border → a square image
            const side = this.tileWidth(idx) - 10;
            return "width: 100%; height: " + side + "px; object-fit: cover;";
        },
        triggerFileInput() {
            this.$refs.fileInput.click();
        },
        handleFileChange(e) {
            const file = e.target.files && e.target.files[0];
            if (file) {
                this.uploadFile(file);
            }
            this.$refs.fileInput.value = "";
        },
        async uploadFile(file) {
            this.errorMsg = "";
            const maxBytes = 5 * 1024 * 1024;

            if (file.size > maxBytes) {
                this.errorMsg = this.$t("errors.upload.file_too_large") || "File too large (max 5MB)";
                return;
            }

            this.uploading = true;
            this.uploadProgress = 0;

            try {
                const presignRes = await AppCatalogUploadService.presign({
                    object_type: this.objectType,
                    file_name: file.name,
                    content_type: file.type
                });

                const { upload_url, cdn_url, object_key } = presignRes.data;

                const xhr = new XMLHttpRequest();
                xhr.upload.addEventListener("progress", (ev) => {
                    if (ev.lengthComputable) {
                        this.uploadProgress = Math.round((ev.loaded / ev.total) * 100);
                    }
                });

                await new Promise((resolve, reject) => {
                    xhr.open("PUT", upload_url);
                    xhr.setRequestHeader("Content-Type", file.type);
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve();
                        } else {
                            reject(new Error("Upload failed: " + xhr.status));
                        }
                    };
                    xhr.onerror = () => reject(new Error("Network error"));
                    xhr.send(file);
                });

                const newImages = [
                    ...this.images,
                    { cdn_url, object_key, sort_order: this.images.length }
                ];
                this.$emit("input", newImages);
            } catch (err) {
                const detail = (err && err.message) ? (": " + err.message) : "";
                this.errorMsg = (this.$t("errors.upload.presign_failed") || "Upload failed") + detail;
            } finally {
                this.uploading = false;
            }
        },
        removeImage(idx) {
            const updated = this.images
                .filter((_, i) => i !== idx)
                .map((img, i) => ({ ...img, sort_order: i }));
            this.$emit("input", updated);
        },
        moveUp(idx) {
            if (idx === 0) return;
            const updated = [...this.images];
            const tmp = updated[idx - 1];
            updated[idx - 1] = updated[idx];
            updated[idx] = tmp;
            this.$emit("input", updated.map((img, i) => ({ ...img, sort_order: i })));
        },
        moveDown(idx) {
            if (idx === this.images.length - 1) return;
            const updated = [...this.images];
            const tmp = updated[idx + 1];
            updated[idx + 1] = updated[idx];
            updated[idx] = tmp;
            this.$emit("input", updated.map((img, i) => ({ ...img, sort_order: i })));
        }
    }
};
</script>
